import React, { useState } from "react";
import { CheckCircle2, Circle, ChevronRight, Loader } from "lucide-react";

export const FOLLOW_UP_CHECKPOINTS = [
  {
    value: "SendConfirmationWhatsApp",
    label: "Send Confirmation WhatsApp",
    labelAr: "إرسال واتساب التأكيد",
  },
  {
    value: "WaitingForTap",
    label: "Waiting For Tap",
    labelAr: "انتظار الضغط",
  },
  {
    value: "WaitingForSecondaryConfirmation",
    label: "Waiting For Secondary Confirmation",
    labelAr: "انتظار التأكيد من الرقم الثاني",
  },
  {
    value: "FirstCall",
    label: "First Call",
    labelAr: "المكالمة الأولى",
  },
  {
    value: "SecondCall",
    label: "Second Call",
    labelAr: "المكالمة الثانية",
  },
];

/** @deprecated legacy — maps to WaitingForTap */
export const LEGACY_CHECKPOINT_ALIASES = {
  SendReminderWhatsApp: "WaitingForTap",
};

export const FOLLOW_UP_CHECKPOINT_VALUES = {
  SEND_CONFIRMATION: "SendConfirmationWhatsApp",
  WAITING_FOR_TAP: "WaitingForTap",
  WAITING_FOR_SECONDARY: "WaitingForSecondaryConfirmation",
  FIRST_CALL: "FirstCall",
  SECOND_CALL: "SecondCall",
};

export const normalizeFollowUpCheckpoint = (checkpoint) => {
  if (LEGACY_CHECKPOINT_ALIASES[checkpoint]) {
    return LEGACY_CHECKPOINT_ALIASES[checkpoint];
  }
  const match = FOLLOW_UP_CHECKPOINTS.find(
    (c) => c.value.toLowerCase() === (checkpoint || "").toLowerCase()
  );
  return match?.value || "SendConfirmationWhatsApp";
};

export const getFollowUpCheckpointIndex = (checkpoint) =>
  FOLLOW_UP_CHECKPOINTS.findIndex((c) => c.value === normalizeFollowUpCheckpoint(checkpoint));

export const getNextFollowUpCheckpoint = (checkpoint) => {
  const index = getFollowUpCheckpointIndex(checkpoint);
  return index >= 0 && index < FOLLOW_UP_CHECKPOINTS.length - 1
    ? FOLLOW_UP_CHECKPOINTS[index + 1].value
    : null;
};

const OrderFollowUpTimeline = ({
  orderId,
  checkpoint,
  onUpdate,
  compact = false,
  className = "",
  readOnly = false,
}) => {
  const [updating, setUpdating] = useState(false);
  const current = normalizeFollowUpCheckpoint(checkpoint);
  const currentIndex = getFollowUpCheckpointIndex(current);
  const nextCheckpoint = getNextFollowUpCheckpoint(current);

  const applyCheckpoint = async (newCheckpoint) => {
    if (readOnly || newCheckpoint === current || updating) return;

    const target = FOLLOW_UP_CHECKPOINTS.find((c) => c.value === newCheckpoint);
    const confirmed = window.confirm(
      `تحديث مرحلة المتابعة إلى:\n${target?.labelAr || newCheckpoint}؟`
    );
    if (!confirmed) return;

    setUpdating(true);
    try {
      await onUpdate(orderId, newCheckpoint);
    } finally {
      setUpdating(false);
    }
  };

  const handleAdvance = () => {
    if (readOnly || !nextCheckpoint) return;
    applyCheckpoint(nextCheckpoint);
  };

  return (
    <div className={`${className}`} dir="rtl">
      <div className={`space-y-1 ${compact ? "text-xs" : "text-sm"}`}>
        {FOLLOW_UP_CHECKPOINTS.map((step, index) => {
          const isCompleted = index < currentIndex;
          const isCurrent = index === currentIndex;
          const isPending = index > currentIndex;

          return (
            <button
              key={step.value}
              type="button"
              disabled={readOnly || updating || isCurrent}
              onClick={() => applyCheckpoint(step.value)}
              className={`w-full flex items-start gap-2 rounded-lg px-2 py-1.5 text-right transition ${
                isCurrent
                  ? "bg-indigo-100 border border-indigo-300 cursor-default"
                  : isCompleted
                  ? `bg-green-50/80 border border-transparent ${readOnly ? "" : "hover:bg-green-100"}`
                  : `bg-gray-50 border border-transparent ${readOnly ? "" : "hover:bg-indigo-50"}`
              } ${updating ? "opacity-60" : ""} ${readOnly && !isCurrent ? "cursor-default" : ""}`}
              title={
                readOnly
                  ? "عرض فقط"
                  : isCurrent
                  ? "المرحلة الحالية"
                  : "اضغط لتعيين هذه المرحلة"
              }
            >
              <span className="mt-0.5 flex-shrink-0">
                {isCompleted ? (
                  <CheckCircle2 size={compact ? 14 : 16} className="text-green-600" />
                ) : (
                  <Circle
                    size={compact ? 14 : 16}
                    className={isCurrent ? "text-indigo-600 fill-indigo-200" : "text-gray-400"}
                  />
                )}
              </span>
              <span
                className={`leading-snug ${
                  isCurrent
                    ? "font-bold text-indigo-800"
                    : isCompleted
                    ? "text-green-800"
                    : isPending
                    ? "text-gray-500"
                    : ""
                }`}
              >
                {step.labelAr}
              </span>
            </button>
          );
        })}
      </div>

      {nextCheckpoint && !readOnly && (
        <button
          type="button"
          disabled={updating}
          onClick={handleAdvance}
          className={`mt-2 w-full flex items-center justify-center gap-1 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 transition disabled:opacity-50 ${
            compact ? "py-1.5 text-xs" : "py-2 text-sm"
          }`}
        >
          {updating ? (
            <Loader size={14} className="animate-spin" />
          ) : (
            <>
              <ChevronRight size={14} />
              المرحلة التالية
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default OrderFollowUpTimeline;
